import dayjs from "dayjs";
import { InputObject } from "../../controls/default";
import { hasValue } from "../../utils/string";
import {
  QueryCondition,
  QueryOperation,
  SearchQuery,
} from "./search-query";

export interface QueryFieldMapping {
  readonly formField: string;
  readonly queryField?: string;
  readonly operation?: QueryOperation;
  readonly transform?: (value: any) => any;
}

export function buildSearchQuery(
  values: Record<string, any>,
  mappings: QueryFieldMapping[],
): SearchQuery {
  const conditions: QueryCondition[] = mappings
    .filter(({ formField }) => hasValue(values?.[formField]))
    .map(({ formField, queryField, operation, transform }) => {
      const rawValue = values[formField];
      const value = transform ? transform(rawValue) : rawValue;
      return {
        field: queryField ?? formField,
        values: Array.isArray(value) ? value : [value],
        operation: operation ?? QueryOperation.StartsWith,
      };
    });

  return { conditions };
}

// Convenience transforms
export const comboboxValue = (v: InputObject) => v.value;
export const comboboxDisplay = (v: InputObject) => v.display;
export const dateValue = (v: any) => dayjs(v).format("YYYY-MM-DD");
export const numberValue = (v: any) => +v;
export const dateRangeValue = (v: any[]) =>
  v.map((d: any) => d.format("YYYY-MM-DDTHH:mm:ssZ"));

// A DATE-only range picker hands back start-of-day for BOTH bounds. Fed straight into a `Between`
// against a stored timestamp, the upper bound becomes 00:00:00 of the closing day and silently drops
// everything recorded later that day — the range looks applied and quietly answers a narrower question.
// This widens the upper bound to end-of-day, so "1st to 5th" means all of the 5th.
// Use this for date-only pickers; `dateRangeValue` stays as-is for pickers that carry a real time.
export const dayRangeValue = (v: any[]) => [
  v[0].startOf("day").format("YYYY-MM-DDTHH:mm:ssZ"),
  v[1].endOf("day").format("YYYY-MM-DDTHH:mm:ssZ"),
];
